import { QUERY_KEYS } from "@/libraries/constant";
import { caseService } from "@/services";
import type {
	BaseCaseDataParams,
	CasePatientVisit,
	GetCaseInfosParams,
	GetCaseInfosResponse,
	GetCasesParams,
} from "@/types";
import {
	type UnusedSkipTokenOptions,
	infiniteQueryOptions,
	queryOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import dayjs from "dayjs";
import isBoolean from "lodash/isBoolean";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";

export const getCasesQueryOptions = (params: Partial<GetCasesParams>) =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_CASES, params],
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			caseService.cases({ ...params, page: pageParam }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (data) => {
			const cases = data.pages?.flatMap((page) => page.cases) ?? [];
			if (!cases.length) return [];
			return orderBy(cases, (c) => c.conversation_updated_at, "desc");
		},
	});

export const getCasesMetaQueryOptions = (params: Partial<GetCasesParams>) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASES_FLAT, params],
		queryFn: () => caseService.cases({ ...params, page: params?.page ?? 1 }),
	});

export const getCaseQueryOptions = (
	id: number,
	enabled?: boolean,
	options?: Omit<
		UnusedSkipTokenOptions<
			Awaited<ReturnType<typeof caseService.case>>,
			AxiosError,
			Awaited<ReturnType<typeof caseService.case>>,
			(number | typeof QUERY_KEYS.GET_CASE)[]
		>,
		"queryKey" | "queryFn" | "enabled" | "select"
	>,
) => {
	return queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE, id],
		queryFn: () => caseService.case(id),
		enabled: isBoolean(enabled) ? enabled && id > 0 : id > 0,
		select: (data) => ({
			...data,
			pending_members: [
				...uniqBy(data.pending_invitations, "id").map((item) => ({
					...item,
					type: "internal" as const,
				})),
				...uniqBy(data.external_pending_invitations, "id").map((item) => ({
					...item,
					type: "external" as const,
				})),
			],
		}),
		...options,
	});
};

export const getCasePatientVisits = (params: BaseCaseDataParams) => {
	return infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_PATIENT_VISITS, params.caseId],
		enabled: !!params.caseId,
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			caseService.patientVisits({ ...params, page: pageParam, limit: 25 }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (res) => {
			const patientVisits =
				res?.pages?.flatMap((page) => page.patient_visits) ?? [];
			if (!patientVisits.length) return [];
			return orderBy(
				patientVisits,
				(pv) => {
					const dateTimeVisit = dayjs(
						`${pv.date} ${pv.time}`,
						"YYYY-MM-DD HH:mm A",
					);
					return dateTimeVisit;
				},
				"desc",
			);
		},
	});
};

export const getCasePatientVisit = (patientVisitId: CasePatientVisit["id"]) => {
	return queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_PATIENT_VISIT, patientVisitId],
		enabled: !!patientVisitId,
		queryFn: () => caseService.patientVisit(patientVisitId),
	});
};

export const getLatestCaseInfo = (
	params: Omit<GetCaseInfosParams, "page" | "limit" | "latest">,
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_INFOS, { ...params, latest: true }],
		queryFn: () =>
			caseService.caseInfos<GetCaseInfosResponse<true>>({
				...params,
				latest: true,
			}),
		enabled: !!params.caseId && !!params.info_type,
	});

export const getCaseInfos = (
	params: Omit<GetCaseInfosParams, "page" | "limit" | "latest">,
) =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_INFOS, { ...params, latest: false }],
		enabled: !!params.caseId && !!params.info_type,
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			caseService.caseInfos<GetCaseInfosResponse<false>>({
				...params,
				latest: false,
				page: pageParam,
				limit: 25,
			}),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (res) => {
			const items =
				res?.pages?.flatMap((page: any) => page.case_info || page.case_infos) ??
				[];
			if (!items.length) return [];
			return orderBy(items, (item) => dayjs(item.created_at), "desc");
		},
	});

export const getCasePatient = (patientIcNumber: string) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_PATIENT, patientIcNumber],
		queryFn: () => caseService.findByPatientId(patientIcNumber),
		enabled: !!patientIcNumber,
	});

export const getCaseMedicalNotes = (caseId: number) =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_MEDICAL_NOTES, caseId],
		initialPageParam: 1,
		queryFn: ({ pageParam = 1, signal }) =>
			caseService.medicalNotes(
				{ caseId, page: pageParam, limit: 25 },
				{ signal },
			),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
	});

export const getCasePdfFile = (
	params: Parameters<typeof caseService.getPdf>[0],
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_PDF_FILE, params],
		queryFn: () =>
			caseService
				.getPdf(params, {
					responseType: "blob",
				})
				.then(
					(res) => new Blob([res.data], { type: res.headers["content-type"] }),
				),
	});

export const getCaseLetterPdfFile = (
	params: Parameters<typeof caseService.getLetterPdf>[0],
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_LETTER_PDF_FILE, params],
		queryFn: () =>
			caseService
				.getLetterPdf(params, {
					responseType: "blob",
				})
				.then(
					(res) => new Blob([res.data], { type: res.headers["content-type"] }),
				),
	});

export const getCaseLetters = (
	params: Parameters<typeof caseService.letters>[0],
) =>
	infiniteQueryOptions({
		initialPageParam: 1,
		queryKey: [QUERY_KEYS.GET_CASE_LETTERS, params],
		queryFn: ({ signal, pageParam }) =>
			caseService.letters(params, {
				signal,
				params: { page: pageParam, limit: 25 },
			}),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
	});

export const getCaseLetter = (
	params: Parameters<typeof caseService.letter>[0],
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_CASE_LETTER, params],
		queryFn: () => caseService.letter(params),
	});
